import { Money } from '@principle-theorem/accounting';
import {
  IVerifonePurchaseTransactionExtendedData,
  IVerifonePurchaseRequest,
  IVerifoneRefundRequest,
  IVerifoneRefundTransactionExtendedData,
  IVerifoneTransactionResponse,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  VerifoneTransactionResult,
  VerifoneTransactionType,
  type IInvoice,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { isNil } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Transaction, getBaseTransaction } from '../transaction/transaction';

export class Verifone {
  static async toPurchaseTransaction(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: IVerifonePurchaseTransactionExtendedData['request'],
    response: IVerifoneTransactionResponse
  ): Promise<ITransaction<IVerifonePurchaseTransactionExtendedData>> {
    const base = await Verifone.toTransaction(
      invoice,
      practiceRef,
      response,
      TransactionType.Incoming
    );
    return Transaction.init({
      ...base,
      extendedData: {
        request,
        response,
      },
    });
  }

  static async toRefundTransaction(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: IVerifoneRefundTransactionExtendedData['request'],
    response: IVerifoneTransactionResponse,
    initialTransaction?: WithRef<
      ITransaction<IVerifonePurchaseTransactionExtendedData>
    >
  ): Promise<ITransaction<IVerifoneRefundTransactionExtendedData>> {
    const base = await Verifone.toTransaction(
      invoice,
      practiceRef,
      response,
      TransactionType.Outgoing
    );
    return Transaction.init({
      ...base,
      reference: initialTransaction?.reference || base.reference,
      extendedData: {
        request,
        response,
      },
    });
  }

  static async toTransaction(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    response: IVerifoneTransactionResponse,
    type: TransactionType
  ): Promise<ITransaction> {
    const amount = Verifone.getTransactionAmount(response);
    return Transaction.init({
      ...(await getBaseTransaction(invoice, type)),
      amount,
      reference: response.reference_id,
      status: Verifone.getStatus(response),
      provider: TransactionProvider.VerifoneCard,
      practiceRef,
    });
  }

  static async toPendingPurchaseTransaction(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: Omit<IVerifonePurchaseRequest, 'referenceId'> & {
      referenceId?: string;
    },
    response?: IVerifoneTransactionResponse
  ): Promise<ITransaction<IVerifonePurchaseTransactionExtendedData>> {
    const syntheticResponse: IVerifoneTransactionResponse = response ?? {
      txn_type: VerifoneTransactionType.Purchase,
      reference_id: request.referenceId ?? uuid(),
      result: VerifoneTransactionResult.Unknown,
    };
    const base = await getBaseTransaction(invoice, TransactionType.Incoming);
    return Transaction.init({
      ...base,
      amount: request.amount,
      reference: syntheticResponse.reference_id,
      status: TransactionStatus.Pending,
      provider: TransactionProvider.VerifoneCard,
      practiceRef,
      extendedData: {
        request,
        response: syntheticResponse,
      },
    });
  }

  static async toPendingRefundTransaction(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: Omit<IVerifoneRefundRequest, 'referenceId'> & {
      referenceId?: string;
    },
    response?: IVerifoneTransactionResponse
  ): Promise<ITransaction<IVerifoneRefundTransactionExtendedData>> {
    const syntheticResponse: IVerifoneTransactionResponse = response ?? {
      txn_type: VerifoneTransactionType.Refund,
      reference_id: request.referenceId ?? uuid(),
      result: VerifoneTransactionResult.Unknown,
    };
    const base = await getBaseTransaction(invoice, TransactionType.Outgoing);
    return Transaction.init({
      ...base,
      amount: request.amount,
      reference: syntheticResponse.reference_id,
      status: TransactionStatus.Pending,
      provider: TransactionProvider.VerifoneCard,
      practiceRef,
      extendedData: {
        request,
        response: syntheticResponse,
      },
    });
  }

  static getTransactionAmount(response: IVerifoneTransactionResponse): number {
    if (!isNil(response.refund_amount)) {
      return Money.amount(response.refund_amount);
    }

    if (!isNil(response.total_amount)) {
      // total_amount includes any tip or surcharge added at the terminal;
      // only the base payment amount should apply to the invoice balance
      const surcharge = response.surcharge_amount ?? 0;
      const tip = response.tip_amount ?? 0;
      return Money.amount(response.total_amount - surcharge - tip);
    }
    return 0;
  }

  static getStatus(response: IVerifoneTransactionResponse): TransactionStatus {
    switch (response.result) {
      case VerifoneTransactionResult.Success:
        return TransactionStatus.Complete;
      case VerifoneTransactionResult.Pending:
        return TransactionStatus.Pending;
      case VerifoneTransactionResult.Failure:
      case VerifoneTransactionResult.Cancelled:
      case VerifoneTransactionResult.Unknown:
      case VerifoneTransactionResult.NoConnection:
      default:
        return TransactionStatus.Failed;
    }
  }

  static isSuccessResult(result: VerifoneTransactionResult): boolean {
    return result === VerifoneTransactionResult.Success;
  }
}
